/**
 * GTM Settings Page
 */
import { useState, useEffect } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';
import {
	Panel,
	PanelBody,
	PanelRow,
	TextControl,
	CheckboxControl,
	Button,
	Spinner,
	Notice,
	__experimentalVStack as VStack,
} from '@wordpress/components';

export default function SettingsPage() {
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [notice, setNotice] = useState(null);
	const [gtmId, setGtmId] = useState('');
	const [excludedRoles, setExcludedRoles] = useState([]);
	const [roles, setRoles] = useState([]);

	useEffect(() => {
		loadSettings();
	}, []);

	// Ctrl+S / Cmd+S to save.
	useEffect(() => {
		const handleKeyDown = (e) => {
			if ((e.ctrlKey || e.metaKey) && e.key === 's') {
				e.preventDefault();
				if (!isSaving && !isLoading) {
					saveSettings();
				}
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [isSaving, isLoading, gtmId, excludedRoles]);

	const loadSettings = async () => {
		setIsLoading(true);
		try {
			const response = await apiFetch({
				path: '/groundworx-gtm/v1/settings',
			});
			setGtmId(response.gtmId || '');
			setExcludedRoles(response.excludedRoles || []);
			setRoles(response.roles || []);
		} catch (error) {
			setNotice({
				status: 'error',
				message: __('Failed to load settings.', 'groundworx-gtm'),
			});
		}
		setIsLoading(false);
	};

	const saveSettings = async () => {
		setIsSaving(true);
		setNotice(null);
		try {
			await apiFetch({
				path: '/groundworx-gtm/v1/settings',
				method: 'POST',
				data: { gtmId, excludedRoles },
			});
			setNotice({
				status: 'success',
				message: __('Settings saved successfully.', 'groundworx-gtm'),
			});
		} catch (error) {
			setNotice({
				status: 'error',
				message: __('Failed to save settings.', 'groundworx-gtm'),
			});
		}
		setIsSaving(false);
	};

	const toggleRole = (roleKey) => {
		setExcludedRoles((prev) =>
			prev.includes(roleKey)
				? prev.filter((r) => r !== roleKey)
				: [...prev, roleKey]
		);
	};

	if (isLoading) {
		return (
			<div className="gwx-gtm-admin-settings--loading">
				<Spinner />
			</div>
		);
	}

	return (
		<div className="gwx-gtm-admin-settings">
			{notice && (
				<Notice
					status={notice.status}
					isDismissible
					onRemove={() => setNotice(null)}
				>
					{notice.message}
				</Notice>
			)}

			<Panel>
				<PanelBody title={__('Container', 'groundworx-gtm')} initialOpen={true}>
					<PanelRow>
						<TextControl
							label={__('GTM Container ID', 'groundworx-gtm')}
							help={__('Enter your Google Tag Manager container ID (e.g. GTM-XXXXXXX).', 'groundworx-gtm')}
							placeholder="GTM-XXXXXXX"
							value={gtmId}
							onChange={setGtmId}
							__next40pxDefaultSize
							__nextHasNoMarginBottom
						/>
					</PanelRow>
				</PanelBody>
			</Panel>

			<Panel>
				<PanelBody title={__('Exclude Roles', 'groundworx-gtm')} initialOpen={true}>
					<PanelRow>
						<VStack spacing={2} style={{ width: '100%' }}>
							<p className="components-base-control__help" style={{ marginTop: 0 }}>
								{__('GTM tracking will not load for users with these roles.', 'groundworx-gtm')}
							</p>
							{roles.map((role) => (
								<CheckboxControl
									key={role.key}
									label={role.name}
									checked={excludedRoles.includes(role.key)}
									onChange={() => toggleRole(role.key)}
									__nextHasNoMarginBottom
								/>
							))}
						</VStack>
					</PanelRow>
				</PanelBody>
			</Panel>

			<div className="gwx-gtm-admin-settings__footer">
				<Button
					variant="primary"
					onClick={saveSettings}
					isBusy={isSaving}
					disabled={isSaving}
				>
					{isSaving ? __('Saving...', 'groundworx-gtm') : __('Save Settings', 'groundworx-gtm')}
				</Button>
			</div>
		</div>
	);
}
